import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Settings, MapPin, Save, AlertCircle, CheckCircle, ArrowLeft, Edit, X } from 'lucide-react';
import { BASE_URL } from '../config';
import { notify } from '../utils/toast';

export default function Setup({ onBack }) {
  const [config, setConfig] = useState({
    user_loc_range: 5,
    user_chose_loc: 10,
  });
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${BASE_URL}/api/setup/config/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setConfig(response.data.config);
      }
    } catch (err) {
      console.error('Error fetching config:', err);
      notify.error('Failed to fetch configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (key, currentValue) => {
    setEditingItem(key);
    setEditValue(currentValue.toString());
  };

  const handleSave = async (key) => {
    const value = parseInt(editValue);
    
    // Validation
    if (value < 1 || value > 100) {
      notify.error('Value must be between 1 and 100 km');
      return;
    }
    
    setSaving(true);
    
    try {
      const token = localStorage.getItem('accessToken');
      const updatedConfig = { ...config, [key]: value };
      
      const response = await axios.post(
        `${BASE_URL}/api/setup/config/update/`,
        updatedConfig,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setConfig(updatedConfig);
        setEditingItem(null);
        setEditValue('');
        notify.success('Configuration updated successfully');
      }
    } catch (err) {
      console.error('Error updating config:', err);
      notify.error('Failed to update configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingItem(null);
    setEditValue('');
  };

  const configItems = [
    {
      key: 'user_loc_range',
      name: 'Maximum Location Range',
      description: 'Maximum distance (in kilometers) for doctor location searches',
      value: config.user_loc_range,
      unit: 'km'
    },
    {
      key: 'user_chose_loc',
      name: 'User Chosen Location Range',
      description: 'Default location range that users can choose for their searches',
      value: config.user_chose_loc,
      unit: 'km'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-medical-500"></div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full"
      >
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
          )}
          <div className="flex items-center gap-3">
            <div className="p-3 bg-medical-100 rounded-lg">
              <Settings className="w-6 h-6 text-medical-600" />
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-slate-800">System Setup</h2>
              <p className="text-sm text-slate-600">Configure location range settings</p>
            </div>
          </div>
        </div>

        {/* Setup Table */}
        <div className="w-full bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">
                    Setup Name
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">
                    Description
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">
                    Current Value
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-slate-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {configItems.map((item) => (
                  <tr key={item.key} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-medical-600" />
                        <span className="font-medium text-slate-900">{item.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-slate-600">{item.description}</span>
                    </td>
                    <td className="px-6 py-4">
                      {editingItem === item.key ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            min="1"
                            max="100"
                            className="w-20 px-2 py-1 border border-slate-300 rounded focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none"
                          />
                          <span className="text-sm text-slate-600">{item.unit}</span>
                        </div>
                      ) : (
                        <span className="font-medium text-slate-900">
                          {item.value} {item.unit}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        {editingItem === item.key ? (
                          <>
                            <button
                              onClick={() => handleSave(item.key)}
                              disabled={saving}
                              className="flex items-center gap-1 px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                            >
                              <Save className="w-4 h-4" />
                              {saving ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              onClick={handleCancel}
                              disabled={saving}
                              className="flex items-center gap-1 px-3 py-1.5 bg-slate-500 hover:bg-slate-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                            >
                              <X className="w-4 h-4" />
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => handleEdit(item.key, item.value)}
                            className="flex items-center gap-1 px-3 py-1.5 bg-medical-500 hover:bg-medical-600 text-white text-sm font-medium rounded-lg transition-colors"
                          >
                            <Edit className="w-4 h-4" />
                            Edit
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Info Box */}
        <div className="mt-6 bg-medical-50 border border-medical-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-medical-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <Settings className="w-5 h-5 text-slate-900" />
            </div>
            <div>
              <h4 className="font-semibold text-medical-900 mb-1">About Location Settings</h4>
              <p className="text-sm text-medical-700">
                These settings control how far users can search for doctors based on their location. 
                The maximum location range sets the upper limit, while the user chosen location range 
                is the default value presented to users. Click "Edit" to modify any setting.
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
